/**
 * PATH: packages/pricelist/src/bulkPricingFilter.ts
 * FILE_ID: PKG-PRICELIST-BULKFILTER-001
 * PURPOSE: Pure helper for the toolbar "Bulk pricing" filter — narrow the rows
 *          shown in the active section to those whose per-SKU bulk_pricing flag
 *          is Yes / No. Mirrors supplierFilter.ts but operates at ROW level
 *          (bulk pricing is a per-SKU property), orthogonal to the section-level
 *          supplier filter.
 * WHY: A SKU with >=2 price tiers (1..N, N+1..M, ...) carries bulk_pricing="Yes"
 *      on every one of its rows (set at import). Operators want to see only the
 *      products that have tiered/bulk pricing.
 * RELATED: columns.ts (GridRow, hoists data.bulk_pricing -> row.bulk_pricing),
 *          PricelistWorkbook.tsx (consumer), supplierFilter.ts (sibling pattern)
 */
import type { GridRow } from './columns'
import type { PricelistSection } from './types'

/** '' = all rows; 'yes' = bulk-priced only; 'no' = non-bulk only. */
export type BulkPricingMode = '' | 'yes' | 'no'

/** True when a row's bulk_pricing flag reads as "Yes" (case-insensitive). */
export function isBulkPriced(row: GridRow): boolean {
  return String(row.bulk_pricing ?? '').trim().toLowerCase() === 'yes'
}

/** Narrow rows by bulk-pricing mode. Empty mode ⇒ rows unchanged. */
export function filterRowsByBulkPricing(rows: GridRow[], mode: BulkPricingMode): GridRow[] {
  if (mode !== 'yes' && mode !== 'no') return rows
  const want = mode === 'yes'
  return rows.filter((r) => isBulkPriced(r) === want)
}

/** First visible section whose loaded rows match the bulk-pricing mode. */
export function firstSectionIdWithBulkPricing(
  sections: PricelistSection[],
  rowsBySection: Record<string, GridRow[]>,
  mode: BulkPricingMode,
): string | null {
  if (mode !== 'yes' && mode !== 'no') return sections[0]?.id ?? null
  for (const section of sections) {
    const rows = rowsBySection[section.id]
    if (rows && filterRowsByBulkPricing(rows, mode).length > 0) return section.id
  }
  return null
}
